import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import type { ReactNode, FormEventHandler } from 'react';

interface FormDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
  submitLabel?: string;
  loading?: boolean;
  /** Disables the submit button independently of `loading`. */
  submitDisabled?: boolean;
  children: ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  /** Optional action rendered on the left side of the actions row, e.g. a Delete button. */
  startAction?: ReactNode;
}

/**
 * Generic dialog used for create/edit forms. Renders the body in a vertical
 * stack so each module's form just lists fields top-to-bottom.
 */
export function FormDialog({
  open,
  title,
  onClose,
  onSubmit,
  submitLabel = 'Save',
  loading,
  submitDisabled,
  children,
  maxWidth = 'sm',
  startAction,
}: FormDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth>
      <form onSubmit={onSubmit} noValidate>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {children}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: startAction ? 'space-between' : 'flex-end' }}>
          {startAction}
          <Stack direction="row" spacing={1}>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={loading || submitDisabled}>
              {submitLabel}
            </Button>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
}
